import { Navigate, useLocation } from 'react-router-dom';
import { getCookie } from './cookieUtils';
import { ACCESS_TOKEN } from './enums/misc';
import { FC, ReactNode } from 'react';
import { checkAccess } from './helpers';
import { PAGE_NOT_FOUND, USER } from 'constant/RoutesUrls';
import { RootState } from 'redux/store';
import { useSelector } from 'react-redux';

interface Props {
  children: ReactNode;
  redirectTo: string;
}

export const RequireAuth: FC<Props> = ({ children, redirectTo }) => {
  const isAuthenticated: boolean = !!getCookie(ACCESS_TOKEN);
  const location = useLocation();

  if (isAuthenticated) return children;

  const currentPath = location.pathname + location.search;
  return (
    <Navigate
      to={{
        pathname: redirectTo,
        search: `?redirect=${encodeURIComponent(currentPath)}`
      }}
    />
  );
};

export const OnlyPublicAuth: FC<Props> = ({ children, redirectTo }) => {
  const isAuthenticated: boolean = !!getCookie(ACCESS_TOKEN);
  const location = useLocation();

  if (!isAuthenticated) return children;

  const params = new URLSearchParams(location.search);
  const redirectUrl = params.get('redirect');
  const destination =
    redirectUrl && redirectUrl.startsWith('/') ? redirectUrl : redirectTo;

  return <Navigate to={destination} />;
};

export const PermissionBasedRoute = ({
  permissionsNeeded,
  children
}: {
  permissionsNeeded: string[];
  children: ReactNode;
}) => {
  const permission = useSelector(
    (state: RootState) => state.userProfile.permission
  );
  const findPermissionModule = (permissionsNeeded: string[]) => {
    if (permissionsNeeded.length === 0) {
      return PAGE_NOT_FOUND;
    }
    const firstPermission = permissionsNeeded[0];
    const permissionModule = firstPermission.split('.')[0];
    return permissionModule;
  };
  const permissionModule = findPermissionModule(permission);
  if (checkAccess(permissionsNeeded, permission)) {
    return children;
  } else {
    return <Navigate to={{ pathname: permissionModule }} />;
  }
};
